import isElectron from 'is-electron';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { deletePushToken, updatePushToken } from '../api/pushTokens';
import { selectUserId } from '../containers/Login/LoginSlice';
import { isDevRelease } from '../config/app';
import { setIncomingMessages } from '../containers/ChatsList/chatListSlice';
import { fetchTotalUnread } from '../containers/ChatsList/chatListSlice/actionCreators';
import { fetchOnCallAlertList } from '../containers/OnCallAlertList/onCallAlertSlice/actionCreators';
import { useAppDispatch } from '../hooks';
import { isDesktop } from '../utils/isDesktop';
import type { AppNotificationParams } from '../shared/notifications';
import { store } from '../store';
import { selectAllTenantSessions } from '../containers/Organizations/OrganizationsSlice';
import { debounce } from 'lodash';
import { localStorageGet, localStorageSet, getItem } from '../utils/storage';
import { isLwtSenderFeaturesEnabled, isMultiOrgFeatureEnabled } from '../utils/features';
import { useNavigate } from 'react-router-dom';
import { fetchAlarmData } from '../containers/LoneWorkerTimer/loneWorkerTimerSlice/actionCreator';
import Emergency1 from './../assets/sounds/emergency.mp3';
import Emergency2 from './../assets/sounds/emergency2.mp3';
import EmergencyPdvSe from './../assets/sounds/emergency_pdv_se.mp3';
import SoundLwtWarning from './../assets/sounds/lwt_warning.mp3';

export const PushyService = (props: {
  onNotification: (notification: AppNotificationParams) => void;
}) => {
  const { onNotification } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const initPushy = () => {
    if (!window.electronApi) {
      console.error('preload.ts initialization failed.');
      return;
    }

    const debouncedFetchUnread = debounce(() => dispatch(fetchTotalUnread()), 2000);

    const soundByFileNameForAlarm: Record<string, string> = {
      'emergency.caf': Emergency1,
      'emergency2.caf': Emergency2,
      'emergency_pdv_se.caf': EmergencyPdvSe,
    };
    const soundByFileNameOnce: Record<string, string> = {
      ...(isLwtSenderFeaturesEnabled() && {
        'lwt_warning.caf': SoundLwtWarning,
      }),
    };
    const soundsToWake: Record<string, true> = {
      ...(isLwtSenderFeaturesEnabled() && {
        'lwt_dispatched.caf': true,
      }),
    };

    const sound = new Audio();
    sound.loop = true;

    // Save device token in the database
    window.electronApi.onRegisterDevice(async token => {
      const deviceToken = localStorageGet('pushyToken');

      if (!token || typeof token !== 'string') {
        console.error('No Pushy token detected!');
        return;
      }

      if (deviceToken !== token) {
        try {
          if (deviceToken) await deletePushToken(deviceToken);
        } catch (error) {
          console.error(error);
        }
      }
      localStorageSet('pushyToken', token);

      if (getItem('coreApiAccessToken') || selectUserId(store.getState())) {
        await updatePushToken(token);
      }
    });

    // Display notification
    window.electronApi.onReceivedNotification(notificationPayload => {
      if (isDesktop() && isDevRelease()) {
        console.log(
          '(-------- dev release only log --------)\n on received_notification [Will Display notification] \n -- notificationPayload: ',
          notificationPayload
        );
      }

      const e2eOneMinute =
        !!process.env.REACT_APP_E2E && !!notificationPayload.personalAlarmOneMinuteWarning;
      const bypassAlarmWindow =
        isLwtSenderFeaturesEnabled() &&
        (!!notificationPayload.personalAlarmOneMinuteWarning ||
          !!notificationPayload.personalAlarmDispatched) &&
        !e2eOneMinute;

      const sessions = isMultiOrgFeatureEnabled() ? selectAllTenantSessions(store.getState()) : [];
      const notificationTenantId = notificationPayload.tenantId
        ? parseInt(notificationPayload.tenantId, 10) || undefined
        : undefined;
      const tenantName = notificationTenantId
        ? sessions.find(session => session.tenantId === notificationTenantId)?.tenantName
        : undefined;
      notificationPayload.tenantName = tenantName;

      if (soundByFileNameForAlarm[notificationPayload.sound]) {
        const alarmName = notificationPayload.title.replace('ALARM - ', '');
        const alarmTenant = sessions.length > 1 ? tenantName : undefined;
        notificationPayload.alarmNotification = {
          alarmTitle: t('desktop_alarm_notification_title'),
          alarmName: alarmName,
          alarmTenant,
          openBtnText: t('desktop_alarm_notification_open_btn_text'),
        };
        notificationPayload.title = t('desktop_alarm_notification_title');
        notificationPayload.message = alarmName;
        sound.src = soundByFileNameForAlarm[notificationPayload.sound];
        sound.play();
      } else if (soundByFileNameOnce[notificationPayload.sound]) {
        const audio = new Audio();
        audio.loop = false;
        audio.src = soundByFileNameOnce[notificationPayload.sound];
        audio.onended = () => (audio.src = '');
        audio.play();
      } else if (soundsToWake[notificationPayload.sound]) {
        window.electronApi?.bringAppToFocus();

        if (notificationPayload.personalAlarmDispatched && isLwtSenderFeaturesEnabled()) {
          const isInLwtScreen = window.location.hash.includes('aloneWorker/timer');
          if (!isInLwtScreen) dispatch(fetchAlarmData(navigate, true));
        }
      }

      if (!bypassAlarmWindow) {
        window.electronApi?.sendHandleNotification(notificationPayload);
      }
    });

    // Show app message
    window.electronApi.onMainMessage(payload => {
      if (isDesktop() && isDevRelease()) {
        console.log(
          '(-------- dev release only log --------)\n on main_message [Show app message] \n -- notification',
          payload
        );
      }
      dispatch(setIncomingMessages(true));
      debouncedFetchUnread();

      if (
        !payload?.notificationPayload ||
        (payload.notificationPayload.sound &&
          !!soundByFileNameForAlarm[payload.notificationPayload.sound])
      ) {
        setTimeout(() => {
          sound.pause();

          sound.src = '';
          sound.load();
        }, 1000);
      }
      if (!payload?.doNotOpenMessages) onNotification(payload);
      if (payload?.isOnCallAlertWindowClose && window.location.hash.includes('onCallAlert')) {
        dispatch(fetchOnCallAlertList());
      }
    });

    window.electronApi.sendPushyInitialized();
  };

  useEffect(() => {
    if (isElectron()) initPushy();
  }, []);

  return null;
};
